import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useReports } from './useReports';
import { usePeriods } from './usePeriods';
import { useUsers } from './useUsers';
import { useTeams } from './useTeams';
import { useReportGroups } from './useReportGroups';
import { ReportFilters } from '../integrations/sqlserver/types';

// ===============================
// CACHED WRAPPER HOOKS
// ===============================

// ✅ Cached Reports Hook - wraps existing useReports
export const useCachedReports = (filters?: ReportFilters, usePagination?: boolean) => {
  const cacheKey = ['reports', 'cached', { filters, usePagination }];
  
  return useQuery({
    queryKey: cacheKey,
    queryFn: async () => {
      // This is a bit hacky, but we'll call the hook inside useQuery
      // We need to use the service directly instead
      const { ReportService } = await import('../integrations/sqlserver');
      
      if (usePagination) {
        return ReportService.getReports(filters);
      } else {
        const data = await ReportService.getAllReports(filters);
        return { data, pagination: null };
      }
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });
};

// ✅ Cached Periods Hook - uses TimePeriods table (Supabase) instead of SQL Server
export const useCachedPeriods = (options: { limit?: number } = {}) => {
  const { limit = 4 } = options;
  const cacheKey = ['periods', 'cached-supabase', { limit }];
  
  return useQuery({
    queryKey: cacheKey,
    queryFn: async () => {
      // Use TimePeriods navigation service instead of SQL Server API
      const { periodNavigationService } = await import('../integrations/supabase/periodNavigationService');
      
      // Get first page of periods from TimePeriods table
      const response = await periodNavigationService.getPeriods(1, limit);
      
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - same as before
    gcTime: 15 * 60 * 1000, // 15 minutes - same as before
    refetchOnWindowFocus: false,
  });
};

// ✅ Cached Users Hook
export const useCachedUsers = (filters?: any) => {
  const cacheKey = ['users', 'cached', { filters }];
  
  return useQuery({
    queryKey: cacheKey,
    queryFn: async () => {
      const { UserService } = await import('../integrations/sqlserver');
      return UserService.getUsers(filters);
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// ✅ Cached Teams Hook
export const useCachedTeams = (filters?: any) => {
  const cacheKey = ['teams', 'cached', { filters }];
  
  return useQuery({
    queryKey: cacheKey,
    queryFn: async () => {
      const { TeamService } = await import('../integrations/sqlserver');
      return TeamService.getTeams(filters);
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// ✅ Cached Report Groups Hook
export const useCachedReportGroups = (filters?: any) => {
  const cacheKey = ['reportGroups', 'cached', { filters }];
  
  return useQuery({
    queryKey: cacheKey,
    queryFn: async () => {
      const { ReportGroupService } = await import('../integrations/sqlserver');
      return ReportGroupService.getReportGroups(filters);
    },
    staleTime: 20 * 60 * 1000, // 20 minutes
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// ===============================
// COMBINED DATA HOOKS
// ===============================

// ✅ Cached Main Page Data - combines all static data
export const useCachedMainPageData = () => {
  const usersQuery = useCachedUsers();
  const teamsQuery = useCachedTeams();
  const reportGroupsQuery = useCachedReportGroups();
  
  return {
    // Data
    users: usersQuery.data || [],
    teams: teamsQuery.data || [],
    reportGroups: reportGroupsQuery.data || [],
    
    // Loading states
    usersLoading: usersQuery.isLoading,
    teamsLoading: teamsQuery.isLoading,
    reportGroupsLoading: reportGroupsQuery.isLoading,
    isAnyLoading: usersQuery.isLoading || teamsQuery.isLoading || reportGroupsQuery.isLoading,
    
    // Error states
    usersError: usersQuery.error?.message,
    teamsError: teamsQuery.error?.message,
    reportGroupsError: reportGroupsQuery.error?.message,
    hasAnyError: !!usersQuery.error || !!teamsQuery.error || !!reportGroupsQuery.error,
    
    // Refetch functions
    refetchUsers: usersQuery.refetch,
    refetchTeams: teamsQuery.refetch,
    refetchReportGroups: reportGroupsQuery.refetch,
    refetchAll: () => {
      usersQuery.refetch();
      teamsQuery.refetch();
      reportGroupsQuery.refetch();
    },
  };
};

// ===============================
// SMART CACHE INVALIDATION
// ===============================

export const useCacheInvalidation = () => {
  const queryClient = useQueryClient();
  
  return {
    // Invalidate specific data types
    invalidateReports: () => queryClient.invalidateQueries({ queryKey: ['reports'] }),
    invalidatePeriods: () => queryClient.invalidateQueries({ queryKey: ['periods'] }),
    invalidateUsers: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
    invalidateTeams: () => queryClient.invalidateQueries({ queryKey: ['teams'] }),
    invalidateReportGroups: () => queryClient.invalidateQueries({ queryKey: ['reportGroups'] }),
    
    // Invalidate all cached data
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['periods'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['reportGroups'] });
    },
    
    // Clear all cache
    clearAll: () => {
      queryClient.clear();
    },
  };
};

// ===============================
// TRANSITION HELPERS
// ===============================

// ✅ Helper hook to gradually migrate from old hooks to cached versions
export const useMigrationHelper = () => {
  return {
    // Check if React Query is available
    isReactQueryAvailable: true,
    
    // Log cache status
    logCacheStatus: () => {
      const queryClient = useQueryClient();
      const cache = queryClient.getQueryCache();
      // Debug: Log cache status for debugging
    },
    
    // Force refresh all data
    forceRefreshAll: () => {
      const { invalidateAll } = useCacheInvalidation();
      invalidateAll();
      // Debug: Force refresh triggered
    },
  };
}; 